import React from 'react';
import NextLink from './Link';
import { useSearchParams, ReadonlyURLSearchParams } from '@framework/next/navigation';

type RetainQueryLinkProps = {
  href: string | { pathname: string, query?: Record<string, string> },
  className?: string,
  children?: React.ReactNode,
  queryItems?: string[] | null,
  dataTest?: string,
};

interface IPathProps {
  href: string | { pathname: string, query?: Record<string, string> },
  currentQueryList: URLSearchParams | ReadonlyURLSearchParams,
  queryNames: RetainQueryLinkProps['queryItems'],
}

const getQueryParams = (href: IPathProps['href']) => {
  if (typeof href === 'object') {
    return [
      href.pathname.split('?')[0],
      new URLSearchParams(href?.query || {}).toString(),
    ];
  }
  return href.split('?');
};

export const usePathParams = ({ href, currentQueryList = new URLSearchParams(), queryNames }: IPathProps) => {
  const [pathname, hrefQuery] = getQueryParams(href);

  const currentQuery = Object.fromEntries(new URLSearchParams(currentQueryList));
  let query = Object.fromEntries(new URLSearchParams(hrefQuery));

  if (queryNames) {
    queryNames.forEach((name) => {
      if (currentQuery[name]) {
        query[name] = currentQuery[name];
      }
    });
  } else {
    query = {
      ...query,
      ...currentQuery,
    };
  }

  return {
    pathname,
    query: new URLSearchParams(query).toString(),
  };
};

const RetainQueryLink = (
  {
    href,
    className,
    children,
    queryItems = ['callbackUrl', 'tenant', 'customParams'],
    ...props
  }: RetainQueryLinkProps,
) => {
  const currentQueryList = useSearchParams();
  const to = usePathParams({
    href,
    queryNames: queryItems,
    currentQueryList,
  });

  return (
    <NextLink
      to={ to }
      className={ className }
      { ...props }
    >
      { children }
    </NextLink>
  );
};

export default RetainQueryLink;
